import {
  ApiProblemError,
  type ProposeRelationshipInput,
  relationshipKeys,
  type RelationshipProposal,
} from '@taler/api-client'
import { useQueryClient } from '@tanstack/vue-query'
import { ref, type Ref } from 'vue'
import { useApi } from '@/app/api'

/** The backend refuses a proposal whose target is the proposing ledger itself. */
export function isProposalToSelf(error: unknown): boolean {
  return error instanceof ApiProblemError && error.code === 'ledger.relationship_proposal_to_self'
}

export interface UseProposeRelationship {
  readonly isSubmitting: Ref<boolean>
  propose(ledgerId: string, input: ProposeRelationshipInput): Promise<RelationshipProposal>
}

export function useProposeRelationship(): UseProposeRelationship {
  const api = useApi()
  const queryClient = useQueryClient()
  const isSubmitting = ref(false)

  return {
    isSubmitting,
    async propose(ledgerId, input) {
      isSubmitting.value = true
      try {
        const proposal = await api.relationships.proposeRelationship(ledgerId, input)
        const keys = relationshipKeys.invalidatedByWrite(ledgerId, proposal.targetLedgerId)
        await Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))
        return proposal
      } finally {
        isSubmitting.value = false
      }
    },
  }
}
